// Helper functions for department and permission checks
// Backend permission format: module.feature.access (e.g. sales.leads.view)

import { useCallback } from 'react';
import { canAccessDepartment, getDepartmentFromSubject, getUserMetadata, parsePermission } from './constants';
import { useAuthStore } from '@stores/auth.store';
import { useAbility } from './AbilityProvider';
import { buildAbilityFromUser } from './ability';
import { MODULE_SIDEBAR_CONFIG, FEATURE_SIDEBAR_CONFIG, getModuleIdFromPath } from './sidebarConfig';

/**
 * Standalone fallback helper to check any permission string cleanly.
 * Never throws ReferenceError: can is not defined.
 */
export const can = (permissionString) => {
    if (!permissionString) return false;
    const user = useAuthStore.getState().user;
    if (!user) return false;
    const parsed = parsePermission(permissionString);
    if (!parsed) return false;
    const [action, subject] = parsed;
    try {
        const ability = buildAbilityFromUser(user);
        return ability?.can?.(action, subject) ?? false;
    } catch {
        return false;
    }
};

/**
 * Hook to check if current user can access a specific department
 */
export const useCanAccessDepartment = () => {
    const user = useAuthStore((state) => state.user);

    return (targetDepartment) => {
        if (!user) return false;
        const { department, role, adminRoles } = getUserMetadata(user);
        return canAccessDepartment(department, targetDepartment, role, adminRoles);
    };
};

/**
 * Hook to get current user's department
 */
export const useUserDepartment = () => {
    const user = useAuthStore((state) => state.user);
    if (!user) return null;
    return getUserMetadata(user).department;
};

/**
 * Hook to check if current user is admin level
 */
export const useIsAdmin = () => {
    const user = useAuthStore((state) => state.user);
    if (!user) return false;
    const { role, adminRoles } = getUserMetadata(user);
    return adminRoles.includes(role);
};

/**
 * Hook to get all user metadata
 */
export const useUserMetadata = () => {
    const user = useAuthStore((state) => state.user);
    return getUserMetadata(user);
};

/**
 * Hook to check resource access taking department scoping into account
 */
export const useCanAccessResource = () => {
    const user = useAuthStore((state) => state.user);

    return (resourceDepartment, permissionString) => {
        if (!user) return false;
        const { department, role, adminRoles } = getUserMetadata(user);

        if (resourceDepartment && !canAccessDepartment(department, resourceDepartment, role, adminRoles)) {
            return false;
        }

        if (permissionString) {
            const parsed = parsePermission(permissionString);
            if (!parsed) return false;
            const [action, subject] = parsed;
            const ability = buildAbilityFromUser(user);
            return ability?.can?.(action, subject) ?? false;
        }

        return true;
    };
};

/**
 * Check if the current user has a backend permission (module.feature.access).
 * Example: useCan('sales.leads.view') -> boolean
 */
export const useCan = (permissionString) => {
    const ability = useAbility();
    if (!permissionString) return false;
    const parsed = parsePermission(permissionString);
    if (!parsed) return false;
    const [action, subject] = parsed;
    try {
        return ability?.can?.(action, subject) ?? false;
    } catch {
        return false;
    }
};

/**
 * Hook that returns a function to check any backend permission string.
 * Example: const can = useCanCheck(); can('sales.leads.create')
 */
export const useCanCheck = () => {
    const ability = useAbility();
    return useCallback(
        (permissionString) => {
            if (!permissionString || !ability || typeof ability?.can !== 'function') return false;
            const parsed = parsePermission(permissionString);
            if (!parsed) return false;
            const [action, subject] = parsed;
            try {
                return ability.can(action, subject) ?? false;
            } catch {
                return false;
            }
        },
        [ability],
    );
};

/**
 * Check if user has at least one permission starting with prefix (e.g. 'sales.', 'projects.').
 */
export const useHasPermissionPrefix = (prefix) => {
    const permissions = useAuthStore((state) => state.user?.permissions) || [];
    if (!prefix) return false;
    return permissions.some((p) => typeof p === 'string' && p.startsWith(prefix));
};

/**
 * Get visible module sidebar items based on CASL ability and user permissions.
 * Returns array of { id, key, icon, label } for the current scope (tenant vs owner).
 */
export const useVisibleModuleItems = (isTenantRoute = false, isOwnerRole = false) => {
    const ability = useAbility();
    const user = useAuthStore((state) => state.user);
    const permissions = user?.permissions || [];

    const canCheck = useCallback(
        (permissionString) => {
            if (!permissionString || !ability || typeof ability?.can !== 'function') return false;
            const parsed = parsePermission(permissionString);
            if (!parsed) return false;
            const [action, subject] = parsed;
            try {
                return ability.can(action, subject) ?? false;
            } catch {
                return false;
            }
        },
        [ability],
    );

    const hasPermission = (permission) => {
        if (!permission) return true;
        if (Array.isArray(permission)) {
            return permission.some((p) => canCheck(p) || permissions.includes(p));
        }
        if (typeof permission === 'string' && permission.endsWith('.')) {
            return permissions.some((p) => typeof p === 'string' && p.startsWith(permission));
        }
        return canCheck(permission) || permissions.includes(permission);
    };

    const moduleList = Array.isArray(MODULE_SIDEBAR_CONFIG) ? MODULE_SIDEBAR_CONFIG : [];

    return moduleList.filter((item) => {
        if (item.id === 'saas' && !isOwnerRole) return false;

        if (item.requiredPermission) {
            return hasPermission(item.requiredPermission);
        }

        const effectivePermissions = item.tenantPermissions && isTenantRoute ? item.tenantPermissions : item.permissions;

        if (effectivePermissions && effectivePermissions.length > 0) {
            return effectivePermissions.some((p) => hasPermission(p));
        }
        return true;
    });
};

/**
 * Get visible feature sidebar items for a module based on CASL ability and user permissions.
 * Returns object { title, features } expected by useInsideLayout.jsx.
 */
export const useVisibleFeatureItems = (currentPath, isTenantRoute = false, isOwnerRole = false) => {
    const ability = useAbility();
    const user = useAuthStore((state) => state.user);
    const permissions = user?.permissions || [];
    const moduleId = getModuleIdFromPath(currentPath);

    const canCheck = useCallback(
        (permissionString) => {
            if (!permissionString || !ability || typeof ability?.can !== 'function') return false;
            const parsed = parsePermission(permissionString);
            if (!parsed) return false;
            const [action, subject] = parsed;
            try {
                return ability.can(action, subject) ?? false;
            } catch {
                return false;
            }
        },
        [ability],
    );

    const hasPermission = (permission) => {
        if (!permission) return true;
        if (Array.isArray(permission)) {
            return permission.some((p) => canCheck(p) || permissions.includes(p));
        }
        if (typeof permission === 'string' && permission.endsWith('.')) {
            return permissions.some((p) => typeof p === 'string' && p.startsWith(permission));
        }
        return canCheck(permission) || permissions.includes(permission);
    };

    const rawFeatures =
        moduleId && Object.prototype.hasOwnProperty.call(FEATURE_SIDEBAR_CONFIG, moduleId)
            ? FEATURE_SIDEBAR_CONFIG[moduleId]
            : [];
    const moduleFeatures = Array.isArray(rawFeatures) ? rawFeatures : [];

    const features = moduleFeatures.filter((item) => {
        if (item.ownerOnly && !isOwnerRole) return false;
        if (typeof item.check === 'function' && !item.check(canCheck)) return false;

        const effectivePermissions = item.tenantPermissions && isTenantRoute ? item.tenantPermissions : item.permissions;

        if (effectivePermissions && effectivePermissions.length > 0) {
            return effectivePermissions.some((p) => hasPermission(p));
        }
        return true;
    });

    return {
        title: moduleId ? (moduleId.charAt(0).toUpperCase() + moduleId.slice(1)).replace(/-/g, ' ') : '',
        features,
    };
};
